<template>
  <Transition name="fade">
    <div v-if="show" class="modal-wrapper" @click.self="closeModal()">
      <div class="auth-modal">
        <div class="auth-modal__close" @click="closeModal()">
          <Icons variant="close" />
        </div>
        <div v-if="!codeSended && !authSuccess" class="auth-modal-section auth-modal_phone">
          <div v-if="authBanner || isKiosk">
            <img :src="isKiosk ? '/img/auth-coins-kiosk.webp' : '/img/auth-coins-tablets.webp'" class="auth-modal__promo-image" alt="">
            <p class="auth-modal__title banner">
              {{ $t('authModal.phoneTitleBanner') }}
            </p>
          </div>
          <p v-else class="auth-modal__title">
            {{ $t('authModal.phoneTitle') }}
          </p>
          <input
            v-model="phone" v-mask="'+7 ### ### ## ##'" type="tel" class="input"
            :class="{ 'input_error': phoneError }" :placeholder="phonePlaceholder" readonly
          >
          <NumberKeyboard v-model="phone" />
        </div>

        <div v-else-if="codeSended && !authSuccess" class="auth-modal-section auth-modal_code">
          <p class="auth-modal__title">
            {{ $t('authModal.codeTitle') }}
          </p>
          <span class="auth-modal__subtitle">
            {{ $t('authModal.codeSubtitle') }} <br>{{ phone }}
          </span>
<div class="auth-modal-code" :class="{ 'auth-modal-code_error': codeError }">
              <div class="auth-modal-code__item">
                <span v-if="code.length <= 0"></span>
                <p v-else>{{ code[0] }}</p>
              </div>
              <div class="auth-modal-code__item">
                <span v-if="code.length <= 1"></span>
                <p v-else>{{ code[1] }}</p>
              </div>
              <div class="auth-modal-code__item">
                <span v-if="code.length <= 2"></span>
                <p v-else>{{ code[2] }}</p>
              </div>
              <div class="auth-modal-code__item">
                <span v-if="code.length <= 3"></span>
                <p v-else>{{ code[3] }}</p>
              </div>
            </div>
            <span v-if="codeError" class="error">{{ codeError }}</span>
            <NumberKeyboard v-model="code" />
        </div>

        <div v-else-if="authSuccess" class="auth-modal-section">
          <p v-if="newUser" class="auth-modal__title">
            {{ $t('authModal.newNameTitle') }}
          </p>
          <p v-else class="auth-modal__title">
            {{ $t('authModal.nameTitle') }}
          </p>
          <span v-if="newUser" class="auth-modal__subtitle" v-html="$t('authModal.newNameSubtitle')"></span>
          <span v-else class="auth-modal__subtitle" v-html="$t('authModal.nameSubtitle')"></span>
          <input v-model="newName" type="text" class="input input_name" :class="{ 'input_error': nameError }" :placeholder="$t('global.name')" readonly>
          <span v-if="nameError" class="error">Недопустимое имя!</span>
          <Keyboard ref="keyboard" v-model="newName" :auto-show="true" />
        </div>

        <div class="row">
          <div v-if="!codeSended && !authSuccess" class="btn btn_outlined" @click="prevStep()">
            {{ $t('global.skip') }}
          </div>
          <div v-if="codeSended && !authSuccess" class="btn btn_outlined" @click="prevStep()">
            {{ $t('global.back') }}
          </div>
          <div class="btn" :class="{ disabled }" @click="nextStep()">
            {{ $t('global.continue') }}
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import Icons from '@/components/UI/Icons/';
import NumberKeyboard from '@/components/UI/NumberKeyboard/'
import Keyboard from '@/components/UI/Keyboard.vue'
import { productProfile, isKiosk } from '@/config/productProfile';

import { useTerminalStore } from '@/stores';
import { captureMessage } from '@/utils/sentry'

const terminalStore = useTerminalStore();
const { t } = useI18n();

const phone = ref('');
const phoneError = ref(false);
const codeSended = ref(false);
const code = ref('');
const codeError = ref('');
const authSuccess = ref(false);
const newUser = ref(false);
const newName = ref('');
const nameError = ref(false);
const disabled = ref(true);

const show = computed(() => terminalStore.authShow);

const authBanner = computed(() => terminalStore.authBanner);

const profile = computed(() => terminalStore.profile);

const phonePlaceholder = computed(() => isKiosk ? '+7 (___) ___ __ __' : '+7');

function setToken(token: string | null) {
    terminalStore.setToken(token);
}

function setAuth(bool: boolean) {
    terminalStore.setAuth(bool);
}

function setAuthBanner(bool: boolean) {
    terminalStore.setAuthBanner(bool);
}

function sendAuthPhone(data: { phone: string; source: string; shop: string | null }) {
    return terminalStore.sendAuthPhone(data);
}

function sendAuthCode(data: { phone: string; code: string }) {
    return terminalStore.sendAuthCode(data);
}

function getProfile() {
    return terminalStore.getProfile();
}

function setProfileData(name: string) {
    return terminalStore.setProfileData(name);
}

function closeModal() {
    phone.value = '';
    phoneError.value = false;
    codeSended.value = false;
    code.value = '';
    codeError.value = '';
    authSuccess.value = false;
    newUser.value = false;

    setAuth(false);
    if (productProfile.authBanner) {
        setAuthBanner(false);
    }
}

function nextStep() {
    if (!codeSended.value && !authSuccess.value) {
        authPhone();
    } else if (codeSended.value && !authSuccess.value) {
        authCode();
    } else if (authSuccess.value) {
        setName();
    }
}

function authPhone() {
    if (phone.value.length < 12) {
        phoneError.value = true;
        return;
    }

    const shop_id = localStorage.getItem('shop_id');

    sendAuthPhone({
        phone: phone.value,
        source: productProfile.authSource,
        shop: shop_id
    })
        .then((result) => {
            if (result.token) {
                authSuccess.value = true;
                setToken(result.token);

                nextTick(() => {
                    getProfile();
                });
            } else if (result.success) {
                codeSended.value = true;
                if (isKiosk) {
                    newUser.value = true;
                }
            } else {
                alert('Ошибка авторизации: ' + result.message);
                captureMessage('Auth error: ' + result.message, {
                    tags: { module: 'auth', action: 'authPhone' },
                });
            }
        })
        .catch((err: unknown) => {
            console.log(err);
            phoneError.value = true;
        });
}

function authCode() {
    sendAuthCode({
        phone: phone.value,
        code: code.value
    })
        .then((result) => {
            if (result.token) {
                codeSended.value = false;
                authSuccess.value = true;
                getProfile();
            } else {
                codeError.value = result.message || 'Ошибка. Попробуйте ещё раз';
                captureMessage('Auth code error: ' + (result.message || ''), {
                    tags: { module: 'auth', action: 'authCode' },
                });
            }
        })
        .catch((err: unknown) => {
            const serverError = (err as { response?: { data?: { error?: string } } }).response?.data;
            if (serverError?.error === 'Wrong verification code') {
                codeError.value = t('authModal.wrongCode');
            } else {
                codeError.value = serverError?.error || 'Ошибка. Попробуйте ещё раз';
            }
        });
}

function setName() {
    if (newName.value.length < 2) {
        nameError.value = true;
    } else {
        setProfileData(newName.value)
            .then((result) => {
                const r = result as { user?: unknown; first_name?: unknown; message?: string } | undefined;
                if (r?.user) {
                    closeModal();
                } else {
                    if (r?.first_name) {
                        nameError.value = true;
                    } else {
                        alert('Ошибка! Имя не сохранено: ' + r?.message);
                        captureMessage('Set name error: ' + r?.message, {
                            tags: { module: 'auth', action: 'setName' },
                        });
                    }
                }
            })
            .catch(() => {
                phoneError.value = true;
            });
    }
}

function prevStep() {
    if (!codeSended.value) {
        closeModal();
    } else if (codeSended.value) {
        codeSended.value = false;
        code.value = '';
        codeError.value = '';
    }
}

watch(phone, (newValue) => {
    if (!codeSended.value && !authSuccess.value) {
        disabled.value = newValue.length !== 16;
    }

    if (phoneError.value) {
        phoneError.value = false;
    }
});

watch(code, (newValue) => {
    if (newValue.length > 4) {
        code.value = newValue.slice(0, 4);
    }
    if (codeError.value) {
        codeError.value = '';
    }
});

watch(profile, (newValue) => {
    if (newValue) {
        newName.value = newValue.first_name ?? '';
    }
});

watch(newName, () => {
    if (nameError.value) {
        nameError.value = false;
    }
});
</script>
<style scoped lang="scss">
.auth-modal {
    position: relative;
    padding: 48px 32px 32px 32px;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 80dvw;
    background: var(--color-white);
    border-radius: 30px;

    [data-product="kiosk"] & {
        max-height: calc(100dvh - 32px);
        overflow-y: auto;
    }

    &-section img.auth-modal__promo-image {
        display: block;
        width: min(440px, 60dvw);
        height: auto;
        max-height: 30dvh;
        margin: 0 auto 16px;
        object-fit: contain;
    }

    &__close {
        position: absolute;
        top: 32px;
        right: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 72px;
        height: 72px;
        border: 2px solid #C6C6C6;
        border-radius: 50%;
    }

    &-section {
        display: flex;
        flex-direction: column;
        align-items: center;
        & span {
            display: flex;
            flex-direction: column;
            align-items: center;
        }
        & img {
            height: 100%;
        }
    }

    &__title {
        margin-bottom: 8px;
        font-size: 28px;
        font-weight: 700;
    }

    &__subtitle {
        margin-bottom: 28px;
        font-size: 18px;
        font-weight: 400;
        text-align: center;
        white-space: pre-line;
    }

    & .input {
        height: 80px;
        text-align: center;
        font-size: 28px;
        font-weight: 700;
        color: var(--color-green);
        border: 2px solid var(--color-light-grey);
        border-radius: 15px;
        outline: none;

        &::placeholder {
            color: var(--color-grey);
        }

        &_name {
            margin-top: 50px;
            text-transform: capitalize;

            &::placeholder {
                font-weight: 400;
                color: var(--color-grey);
            }
        }

        &_error {
            color: var(--color-red);
        }
    }

    &-section &-code {
        margin-top: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 38px;
        height: 48px;
        font-size: 22px;
        font-weight: 700;

        &_error {
            color: var(--color-red);
        }

        &__item span {
            display: block;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: var(--color-grey);
        }
    }

    & .number-keyboard {
        margin-top: 48px;
    }

    & .row {
        margin-top: 72px;
        display: flex;
        align-items: center;
        gap: 16px;
        width: 100%;
    }

    & .error {
        margin-top: 12px;
        color: var(--color-red);
        font-weight: 600;
    }

    & .btn.disabled {
        color: var(--color-green);
        background: #D3F3EA;
        cursor: not-allowed;
    }
}
</style>